import { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import axios from 'axios';
import { getAuthHeader } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Edit, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', kot_printer_id: '' });
  const [subForm, setSubForm] = useState({ name: '', category_id: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
    fetchPrinters();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { headers: getAuthHeader() });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`${API}/subcategories`, { headers: getAuthHeader() });
      setSubcategories(response.data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchPrinters = async () => {
    try {
      const response = await axios.get(`${API}/printers/configured`, { headers: getAuthHeader() });
      setPrinters(response.data.printers || []);
    } catch (error) {
      console.error('Error fetching printers:', error);
      setPrinters([]);
    }
  };

  const handleCategorySubmit = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory}`, catForm, { headers: getAuthHeader() });
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/categories`, catForm, { headers: getAuthHeader() });
        toast.success('Category created successfully');
      }
      setCatOpen(false);
      setCatForm({ name: '', kot_printer_id: '' });
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const handleEditCategory = (category) => {
    setCatForm({ 
      name: category.name, 
      kot_printer_id: category.kot_printer_id || '' 
    });
    setEditingCategory(category.id);
    setCatOpen(true);
  };

  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubcategory) {
        await axios.put(`${API}/subcategories/${editingSubcategory}`, subForm, { headers: getAuthHeader() });
        toast.success('Subcategory updated successfully');
      } else {
        await axios.post(`${API}/subcategories`, subForm, { headers: getAuthHeader() });
        toast.success('Subcategory created successfully');
      }
      setSubOpen(false);
      setSubForm({ name: '', category_id: '' });
      setEditingSubcategory(null);
      fetchSubcategories();
    } catch (error) {
      console.error('Error saving subcategory:', error);
      toast.error('Failed to save subcategory');
    }
  };

  const handleEditSubcategory = (subcategory) => {
    setSubForm({ name: subcategory.name, category_id: subcategory.category_id });
    setEditingSubcategory(subcategory.id);
    setSubOpen(true);
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure? This will affect menu items.')) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { headers: getAuthHeader() });
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleDeleteSubcategory = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/subcategories/${id}`, { headers: getAuthHeader() });
      toast.success('Subcategory deleted');
      fetchSubcategories();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      toast.error('Failed to delete subcategory');
    }
  };

  const reorderCategory = async (categoryId, direction) => {
    try {
      await axios.patch(`${API}/categories/${categoryId}/reorder?direction=${direction}`, {}, { headers: getAuthHeader() });
      toast.success('Category reordered');
      fetchCategories();
    } catch (error) {
      console.error('Error reordering category:', error);
      toast.error('Failed to reorder category');
    }
  };

  const updateCategoryPosition = async (categoryId, newPosition) => {
    try {
      await axios.patch(`${API}/categories/${categoryId}/reorder?position=${newPosition}`, {}, { headers: getAuthHeader() });
      toast.success('Category position updated');
      fetchCategories();
    } catch (error) {
      console.error('Error updating position:', error);
      toast.error('Failed to update position');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat ? cat.name : 'Unknown';
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Categories</h1>
          <p className="text-gray-600">Organize your menu items</p>
        </div>

        {/* Categories Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Categories</h2>
            <Dialog open={catOpen} onOpenChange={(open) => {
              setCatOpen(open);
              if (!open) {
                setCatForm({ name: '', kot_printer_id: '' });
                setEditingCategory(null);
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-category-button"
                  className="bg-gradient-to-r from-blue-500 to-cyan-500"
                >
                  <Plus size={20} className="mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCategorySubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="cat_name">Category Name</Label>
                    <Input
                      id="cat_name"
                      data-testid="category-name-input"
                      value={catForm.name}
                      onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="kot_printer">Assign KOT Printer (Optional)</Label>
                    <select
                      id="kot_printer"
                      value={catForm.kot_printer_id}
                      onChange={(e) => setCatForm({ ...catForm, kot_printer_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">-- Default Printer --</option>
                      {Array.isArray(printers) && printers.filter(p => p.printer_type === 'kot' && p.is_active).map(printer => (
                        <option key={printer.printer_id} value={printer.printer_id}>
                          {printer.printer_name || printer.printer_id}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Items in this category will print to this specific KOT printer
                    </p>
                  </div>
                  
                  <Button type="submit" data-testid="submit-category-button" className="w-full">
                    {editingCategory ? 'Update' : 'Create'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat, index) => (
              <Card key={cat.id} className="border-0 shadow-md" data-testid="category-card">
                <CardHeader>
                  <CardTitle className="flex justify-between items-center flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="1"
                        max={categories.length}
                        value={index + 1}
                        onChange={(e) => {
                          const newPos = parseInt(e.target.value);
                          if (newPos > 0 && newPos <= categories.length) {
                            updateCategoryPosition(cat.id, newPos);
                          }
                        }}
                        className="w-16 text-center font-bold"
                        title="Position/Rank"
                      />
                      <span className="text-lg">{cat.name}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => reorderCategory(cat.id, 'up')}
                        disabled={index === 0}
                        title="Move Up"
                      >
                        <ArrowUp size={16} className="text-gray-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => reorderCategory(cat.id, 'down')}
                        disabled={index === categories.length - 1}
                        title="Move Down"
                      >
                        <ArrowDown size={16} className="text-gray-600" />
                      </Button>
                      <Button
                        data-testid="edit-category-button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditCategory(cat)}
                      >
                        <Edit size={16} className="text-blue-600" />
                      </Button>
                      <Button
                        data-testid="delete-category-button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(cat.id)}
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                {cat.kot_printer_id && (
                  <CardContent>
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold">KOT Printer:</span>{' '}
                      {Array.isArray(printers) && printers.find(p => p.printer_id === cat.kot_printer_id)?.printer_name || cat.kot_printer_id.substring(0, 8)}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Subcategories Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Subcategories</h2>
            <Dialog open={subOpen} onOpenChange={(open) => {
              setSubOpen(open);
              if (!open) {
                setSubForm({ name: '', category_id: '' });
                setEditingSubcategory(null);
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-subcategory-button"
                  className="bg-gradient-to-r from-purple-500 to-pink-500"
                >
                  <Plus size={20} className="mr-2" />
                  Add Subcategory
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingSubcategory ? 'Edit Subcategory' : 'Create Subcategory'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubcategorySubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="parent_cat">Parent Category</Label>
                    <select
                      id="parent_cat"
                      data-testid="subcategory-parent-select"
                      className="w-full p-2 border rounded-md"
                      value={subForm.category_id}
                      onChange={(e) => setSubForm({ ...subForm, category_id: e.target.value })}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="sub_name">Subcategory Name</Label>
                    <Input
                      id="sub_name"
                      data-testid="subcategory-name-input"
                      value={subForm.name}
                      onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" data-testid="submit-subcategory-button" className="w-full">
                    {editingSubcategory ? 'Update' : 'Create'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subcategories.map((sub) => (
              <Card key={sub.id} className="border-0 shadow-md" data-testid="subcategory-card">
                <CardHeader>
                  <CardTitle className="flex justify-between items-center text-base">
                    <div>
                      <div>{sub.name}</div>
                      <div className="text-xs text-gray-500 font-normal">
                        {getCategoryName(sub.category_id)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        data-testid="edit-subcategory-button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditSubcategory(sub)}
                      >
                        <Edit size={16} className="text-blue-600" />
                      </Button>
                      <Button
                        data-testid="delete-subcategory-button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSubcategory(sub.id)}
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}